import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DataUtils } from '@iupics-util/tools/data.utils';
import { signalSlice } from '@iupics/apiz-grid';
import {
  catchError,
  concat,
  concatMap,
  delay,
  filter,
  finalize,
  ignoreElements,
  interval,
  map,
  Observable,
  of,
  scan,
  take,
  tap,
} from 'rxjs';

@Injectable()
export class AiChatDataService {
  state = signalSlice({
    initialState: this.getInitialState(),
    sources: [
      this.retrieveChats().pipe(
        map((chats) => {
          const chat = this.getNewChat();
          return { chats: [...chats, chat], current: chat.id, loading: false };
        })
      ),
    ],
    actionSources: {
      sendMessage: (state, actions$: Observable<{ chat: Chat; message: Message }>) => {
        return actions$.pipe(
          map((entry) => {
            const update = this.updateChat(state().chats, entry);

            if (update.valid === false) {
              return { errors: update.errors };
            }

            return { chats: update.data, loading: true };
          })
        );
      },
      createChat: (state, action$: Observable<void>) => {
        return action$.pipe(
          map(() => {
            const chats = state().chats.slice(0, -1);
            const lastChat = state().chats.at(-1);

            if (lastChat.messages.length > 0) {
              chats.push(lastChat);
            }

            const newChat = this.getNewChat();
            return {
              chats: [...chats, newChat],
              current: newChat.id,
            };
          })
        );
      },
      setCurrent: (state, action$: Observable<Chat['id']>) => {
        return action$.pipe(map((id) => ({ current: id })));
      },
      setLoading: (state, action$: Observable<boolean>) => {
        return action$.pipe(map((value) => ({ loading: value })));
      },
    },
    selectors: (state) => ({
      chat: (): Chat => state.chats().find((c) => c.id === state.current()),
    }),
  });

  constructor() {
    (this.state.sendMessage$ as Observable<{ chat: Chat; message: Message }>)
      .pipe(
        filter((e) => e.message.createdBy === ChatUser.USER),
        tap((entry) => this.sendToAI(entry.chat)),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  private getInitialState(): AiChatDataState {
    return {
      chats: [],
      current: undefined,
      loading: true,
      errors: undefined,
    };
  }

  private retrieveChats(): Observable<Chat[]> {
    // If we persist chats
    return of([]);
  }

  private makeIARequest(): Observable<string> {
    // TODO: connect to AI API
    return this.streamLoremText();
  }

  private async sendToAI(chat: Chat): Promise<void> {
    this.state.sendMessage({
      chat,
      message: {
        id: DataUtils.uuid(),
        content$: this.makeIARequest(),
        created: new Date(),
        createdBy: ChatUser.ASSISTANT,
      },
    });
  }

  private updateChat(chats: Chat[], { chat, message }: { chat: Chat; message: Message }): Validation<Chat[]> {
    const destination = chats.find((c) => c.id === (chat?.id ?? chat));

    if (!destination) {
      /* TODO: translate */
      return { valid: false, errors: ['No chat found for this message.'] };
    }

    if (!('id' in message)) {
      message['id'] = DataUtils.uuid();
    }

    if (message.createdBy === ChatUser.ASSISTANT) {
      message.content$ = this.getTypewriterEffect(message.content$).pipe(
        catchError((error) => {
          this.state.sendMessage({
            chat,
            message: this.getNewMessage(error, ChatUser.SYSTEM),
          });

          return '';
        }),
        finalize(() => this.state.setLoading(false))
      );
    }

    destination.messages = [...destination.messages, message];

    return { valid: true, data: [...chats.map((c) => ({ ...c }))] };
  }

  private getNewChat(): Chat {
    return {
      id: DataUtils.uuid(),
      messages: [],
      created: new Date(),
    };
  }

  private getNewMessage(content: Observable<string> | string, createdBy: ChatUser): Message {
    return {
      id: DataUtils.uuid(),
      content$: typeof content === 'string' ? of(content) : content,
      created: new Date(),
      createdBy,
    };
  }

  private getTypewriterEffect(chunks$: Observable<string>): Observable<string> {
    return chunks$.pipe(
      concatMap((chunk) => this.typeChars(` ${chunk}`, 5, 100)),
      scan((acc: string, char: string) => acc + char, '')
    );
  }

  private typeChars(word: string, speed: number, pauseMs: number): Observable<string> {
    return concat(
      interval(speed).pipe(
        take(word.length),
        map((i) => word.charAt(i))
      ),
      of(null).pipe(delay(pauseMs), ignoreElements())
    );
  }

  // TODO: remove after connected to IA API
  private base =
    'Lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua';
  private words = this.base.split(' ');

  /**
   * Simule un stream de fragments Lorem Ipsum en émettant des morceaux périodiquement.
   * @param chunkSize Nombre de mots par fragment
   * @param intervalMs Intervalle entre chaque fragment en ms
   * @param totalChunks Nombre total de fragments à émettre
   */
  streamLoremText(chunkSize = 20, intervalMs = 500, totalChunks = 4): Observable<string> {
    return interval(intervalMs).pipe(
      delay(500),
      take(totalChunks),
      map(() => this.makeChunk(chunkSize))
    );
  }

  /** Construit un fragment de Lorem de `count` mots en puisant aléatoirement dans `this.words`. */
  private makeChunk(count: number): string {
    const result: string[] = [];
    for (let i = 0; i < count; i++) {
      const idx = Math.floor(Math.random() * this.words.length);
      result.push(this.words[idx]);
    }
    return result.join(' ') + '.';
  }
}

type ChatUser = 'user' | 'assistant' | 'system';
export const ChatUser: Record<Uppercase<ChatUser>, ChatUser> = {
  USER: 'user',
  ASSISTANT: 'assistant',
  SYSTEM: 'system',
} as const;

export type Message = {
  id?: string | number;
  content?: string[];
  content$: Observable<string>;
  created: Date;
  createdBy: ChatUser;
};

export type Chat = {
  id: string | number;
  messages: Message[];
  created: Date;
};

type AiChatDataState = {
  chats: Chat[];
  current: Chat['id'];
  errors: string[] | undefined;
  loading: boolean;
};

type Validation<T> = { valid: false; errors: AiChatDataState['errors'] } | { valid: true; data: T };
